/**
 * A roster as a list of instruments, one entry a player, counted and edited.
 *
 * A roster is shown as how many of each instrument it has, so every place that shows one
 * counts it the same way, and every place that edits one keeps its players of a kind together:
 * a second violin goes beside the first, not after the cello.
 */

/** How many of each instrument `roster` has, in the order each is first named. */
export function tally(roster: readonly string[]): Map<string, number> {
  const found = new Map<string, number>();
  for (const one of roster) found.set(one, (found.get(one) ?? 0) + 1);
  return found;
}

/** Whether `one` and `other` seat the same players in the same order. */
export function sameSeats(one: readonly string[], other: readonly string[]): boolean {
  return one.length === other.length && one.every((seat, at) => seat === other[at]);
}

/** `roster` with one more `instrument`: beside the last of its kind, or last where it has none. */
export function oneMore(roster: readonly string[], instrument: string): string[] {
  const last = roster.lastIndexOf(instrument);
  const at = last < 0 ? roster.length : last + 1;
  return [...roster.slice(0, at), instrument, ...roster.slice(at)];
}

/** `roster` without the last of its `instrument`s, or as it is where it has none. */
export function oneFewer(roster: readonly string[], instrument: string): string[] {
  const at = roster.lastIndexOf(instrument);
  return at < 0 ? [...roster] : [...roster.slice(0, at), ...roster.slice(at + 1)];
}
